import Enumerable from "linq";

import { IReceptionSaveResultBean, IReceptionDisplayBean } from "../beans";
import { IReceptionListDisplayBaseBean, IReceptionSaveResultBaseBean } from "../beans/base";
import {
  IFormatInfoBean,
  IPatientAddressBean,
  IFlagBean,
  IButtonBean,
  IWorkflowDestinationBean,
  IWorkflowOperatorBean,
  IWorkflowStatusBean,
  IWorkflowStatusNameBean,
  IDocumentCreateButtonBean,
  IDateRangeBean,
  IReceptionTypeItemBean,
} from "../beans/items";
import { IDispatchDocumentResultBean } from "../beans/document";
import { IReceptionCommentDisplayBean } from "../beans/reference";

import { IPatientBean } from "../../../patient/ver2/beans";
import { IUserBean } from "../../../user/ver2/beans";
import { IDepartmentBean } from "../../../department/ver2/beans";
import { DocumentPreviewSource, DocumentPrintSource, ObjectTranslator 
} from "../../../document/ver2";
import { PatientRepository } from "../../../patient/ver2";
import { HisInformationTranslator } from "../../../his/ver2";
import { IHisInformationBean, IHisInformationItemBean } from "../../../his/ver2/beans";
import { FormatPreviewSource, FormatThumbnailSource } from "../../../format/ver2";
import { IDocumentSummaryBean, IDocumentSummaryHistoryBean, IReferenceFileInformationBean, IUnmanagedDocumentSummaryInformationBean } from "../../../document/ver2/beans";

import { ReceptionSaveResult, ReceptionDispInfo, ReceptionDateTranslator } from "../../../../domain/workflow";
import { DispatchDocumentResult } from "../../../../domain/workflow/document";
import { ReceptionListDispInfoBase, ReceptionSaveResultBase } from "../../../../domain/workflow/base";
import { ReceptionCommentDispInfo } from "../../../../domain/workflow/reference";
import {
  PatientAddress,
  ReceptionFlag,
  ButtonInfo,
  WorkflowDestination,
  WorkflowOperator,
  WorkflowStatus,
  WorkflowStatusName,
  DocumentCreateButtonInfo,
  ReceptionTypeItem,
} from "../../../../domain/workflow/items";

import { FormatReference, Company } from "../../../../domain/format";
import { Patient } from "../../../../domain/patient";
import { User, Profession } from "../../../../domain/user";
import { Department } from "../../../../domain/department";
import { DocumentIdentifier, DocumentSummary, DocumentSummaryHistory, ReferenceFileInformation, UnmanagedDocumentSummaryInformation } from "../../../../domain/document";
import { HisInformation, HisInformationItem } from "../../../../domain/his";
import { DateDuration } from "../../../../domain/system";
import { IProjectTeamBean } from "../../../projectTeam/ver2/beans";
import { ProjectTeamTranslator } from "../../../projectTeam/ver2";
import { ProjectTeam } from "../../../../domain/projectTeam";


export class ReceptionBeanBaseTranslator {
  constructor(protected _getToken: () => string) {}

  protected _dateTranslator: ReceptionDateTranslator = new ReceptionDateTranslator();
  protected _documentTranslator: ObjectTranslator = new ObjectTranslator(this._getToken);
  private _patientTranslator: PatientRepository = new PatientRepository(this._getToken);
  private _hisInformationTranslator: HisInformationTranslator = new HisInformationTranslator();

  protected setReceptionListDispInfoBase(bean: IReceptionListDisplayBaseBean, model: ReceptionListDispInfoBase): void {

    model.labelSetting = this.toReceptionDispInfo(bean.labelSetting);
    model.commentSetting = this.toReceptionCommentDispInfo(bean.commentSetting);
    model.isRequestSheetEnable = bean.isRequestSheetEnable;
    model.loadingChunkSize = bean.loadingChunkSize;
    model.documentCreateButtons = this.toDocumentCreteBunttonInfos(bean.documentCreateButtons);
    model.isNewReceptionButtonEnable = bean.isNewReceptionButtonEnable;
    model.isDocumentListButtonEnable = bean.isDocumentListButtonEnable;
    model.isManagerHistorySearchEnable = bean.isManagerHistorySearchEnable;
    model.defaultSelectedContentTab = bean.defaultSelectedContentTab;
    model.shouldTakeOverPatientNoFromMainView = bean.shouldTakeOverPatientNoFromMainView;
    model.shouldShowManagerPhoneNumber = bean.shouldShowManagerPhoneNumber;
    model.shouldShowCsvDownloadButton = bean.shouldShowCsvDownloadButton;
    model.shouldShowListPrintButton = bean.shouldShowListPrintButton;
  }

  public toReceptionSaveResult(bean: IReceptionSaveResultBean): ReceptionSaveResult {
    if (!bean) { return null; }
    const model = new ReceptionSaveResult();
    this.setReceptionSaveResultBase(bean, model);

    return model;
  }

  protected toDocumentCreteBunttonInfos(beans: IDocumentCreateButtonBean[]): DocumentCreateButtonInfo[] {
    return Enumerable.from(beans)
    .select(bean => this.toDocumentCreateButtonInfo(bean))
    .toArray();
  }

  protected toDocumentCreateButtonInfo(bean: IDocumentCreateButtonBean): DocumentCreateButtonInfo {
    if (!bean) { return null; }
    const model = new DocumentCreateButtonInfo(
      bean.buttonName,
      bean.formatOriginalId
    );

    return model;
  }

  protected setReceptionSaveResultBase(bean: IReceptionSaveResultBaseBean, model: ReceptionSaveResultBase): void {
    model.success = bean.success;
    model.id = bean.id;
    model.message = bean.message;
    model.reason = bean.reason;
    model.archiveId = bean.archiveId;
    model.isCommentRegistrationFailure = bean.isCommentRegistrationFailure;
    model.commentId = bean.commentId;
  }

  // 文書ディスパッチ結果
  public toDispatchDocumentResult(bean: IDispatchDocumentResultBean): DispatchDocumentResult {
    const model = new DispatchDocumentResult();
    model.manageType = bean.manageType;
    model.subType = bean.subType;
    model.targetId = bean.targetId;
    model.isEditable = bean.isEditable;

    return model;
  }

  // フラグ
  protected toReceptionFlag(bean: IFlagBean): ReceptionFlag {
    if (!bean) { return null; }
    return new ReceptionFlag(bean.name, bean.value);
  }

  // 依頼表示設定（共通）
  protected toReceptionDispInfo(bean: IReceptionDisplayBean): ReceptionDispInfo {
    const model = new ReceptionDispInfo();

    if (bean) {
      model.commentLabelName = bean.commentLabel;
      model.restrictCommentLabelName = bean.restrictCommentLabel;
      model.nextMessageLabelName = bean.nextMessageLabel;
      model.backMessageLabelName = bean.backMessageLabel;
      model.contactedCommentLabelName = bean.contactedCommentLabel;
      model.delCommentLabelName = bean.delCommentLabel;
      model.generalFlag1LabelName = bean.generalFlag1Label;
      model.isApplicantVisible = bean.isApplicantVisible;
      model.isRecipientVisible = bean.isRecipientVisible;
      model.isContactVisible = bean.isContactVisible;
      model.isProjectTeamVisible = bean.isProjectTeamVisible;
      model.isPaperNameVisible = bean.isPaperNameVisible;
      model.isPaperCompanyNameVisible = bean.isPaperCompanyNameVisible;
      model.isGeneralFlag1Visible = bean.isGeneralFlag1Visible;
      model.isInOutTypeVisible = bean.isInOutTypeVisible;
      model.isAddressTypeVisible = bean.isAddressTypeVisible;
      if (bean.paymentStatusList) {
        model.paymentStatusList = this.toReceptionTypeItems(bean.paymentStatusList);
      }
      model.isDiseaseVisible = bean.isDiseaseVisible;
    }

    return model;
  }

  protected toReceptionTypeItems(beans: IReceptionTypeItemBean[]): ReceptionTypeItem[] {
    return Enumerable.from(beans)
      .select(b => this.toReceptionTypeItem(b))
      .toArray();
  }

  private toReceptionTypeItem(bean: IReceptionTypeItemBean): ReceptionTypeItem {
    return new ReceptionTypeItem(bean.code, bean.name, bean.isDisable);
  }

  // コメント表示設定
  protected toReceptionCommentDispInfo(bean: IReceptionCommentDisplayBean): ReceptionCommentDispInfo {
    const model = new ReceptionCommentDispInfo();

    if (bean) {
      model.commentMode = bean.commentMode;
      model.restrictCommentMode = bean.restrictCommentMode;
      model.nextMessageMode = bean.nextMessageMode;
      model.contactedCommentMode = bean.contactedCommentMode;
      model.delCommentMode = bean.delCommentMode;
      model.commentDefaultSortType = bean.commentDefaultSortType;
      model.isAdminCommentTabEnable = bean.isAdminCommentTabEnable;
      model.isRestrictCommentEnable = bean.isRestrictCommentEnable;
      model.isContactedCommentEnable = bean.isContactedCommentEnable;
      model.isDelCommentEnable = bean.isDelCommentEnable;
      model.isCommentRegisterable = bean.isCommentRegisterable;
      model.isRestrictCommentRegisterable = bean.isRestrictCommentRegisterable;
      model.isNextMessageRegisterable = bean.isNextMessageRegisterable;
      model.isContactedCommentRegisterable = bean.isContactedCommentRegisterable;
      model.isDelCommentRegisterable = bean.isDelCommentRegisterable;
      model.shouldShowCommentsMixed = bean.shouldShowCommentsMixed;
      model.commentsMixedDefaultAdditionalCommentType = bean.commentsMixedDefaultAdditionalCommentType;
      model.mixedCommentBackgroundColor = bean.mixedCommentBackgroundColor;
      model.mixedRestrictedBackgroundColor = bean.mixedRestrictedBackgroundColor;
      model.mixedNextMessageBackgroundColor = bean.mixedNextMessageBackgroundColor;
      model.mixedBackMessageBackgroundColor = bean.mixedBackMessageBackgroundColor;
      model.mixedContactedInfoBackgroundColor = bean.mixedContactedInfoBackgroundColor;
      model.mixedDelCommentBackgroundColor = bean.mixedDelCommentBackgroundColor;
      model.mixedConflictBackgroundColor = bean.mixedConflictBackgroundColor;
      model.shouldSortCommentAsc = bean.shouldSortCommentAsc;
    }

    return model;
  }

  // ボタン設定
  protected toButtonInfo(bean: IButtonBean, defaultButtonName: string): ButtonInfo {
    if (!bean) { return null; }

    const model = new ButtonInfo();
    model.isPermitted = bean.isPermitted;
    model.triggerCode = bean.triggerCode;
    model.buttonName = bean.buttonName ? bean.buttonName : defaultButtonName;
    model.destinations = this.toWorkflowDestinations(bean.destinations);
    model.confirmMessage = bean.confirmMessage;

    return model;
  }

  // WF行き先
  protected toWorkflowDestinations(beans: IWorkflowDestinationBean[]): WorkflowDestination[] {
    return Enumerable.from(beans)
      .select(bean => this.toWorkflowDestination(bean))
      .toArray();
  }

  protected toWorkflowDestination(bean: IWorkflowDestinationBean): WorkflowDestination {
    if (!bean) { return null; }

    const model = new WorkflowDestination();
    model.statusName = bean.statusName;
    model.phaseId = bean.phaseId;
    model.actionId = bean.actionId;
    model.actor = this.toUser(bean.actor);
    model.operationTime = this._dateTranslator.fromDateTimeString(bean.operationTime);
    model.operator = this.toWorkflowOperator(bean.operator);

    return model;
  }

  // WF実施者
  protected toWorkflowOperator(bean: IWorkflowOperatorBean): WorkflowOperator {
    if (!bean) { return null; }

    const model = new WorkflowOperator();
    model.user = this.toUser(bean.user);
    model.professionName = bean.professionName;

    return model;
  }

  // WF状態
  protected toWorkflowStatus(bean: IWorkflowStatusBean): WorkflowStatus {
    if (!bean) { return null; }

    const model = new WorkflowStatus();
    model.status = this.toWorkflowStatusName(bean.status);
    model.order = bean.order;
    model.limitDate = this._dateTranslator.fromDateString(bean.limitDate);

    return model;
  }

  // WF状態名設定
  protected toWorkflowStatusName(bean: IWorkflowStatusNameBean): WorkflowStatusName {
    if (!bean) { return null; }
    return new WorkflowStatusName(
      bean.code,
      bean.name,
      bean.waitName,
      bean.doneName,
      bean.type
    );
  }

  protected toWorkflowStatusNames(beans: IWorkflowStatusNameBean[]): WorkflowStatusName[] {
    return Enumerable.from(beans)
    .select(bean => this.toWorkflowStatusName(bean))
    .toArray();
  }



  // ----------------- 共通 ---------------------------------
  // 患者
  protected toPatient(bean: IPatientBean): Patient {
    if (!bean) { return null; }

    return this._patientTranslator.toPatient(bean);
  }

  protected toPatientAddress(bean: IPatientAddressBean): PatientAddress {
    if (!bean) { return null; }

    const model = new PatientAddress();
    model.phoneNumber = bean.phoneNumber;
    model.zipCode = bean.zipCode;
    model.address = bean.address;

    return model;
  }

  protected toHisInformation(bean: IHisInformationBean): HisInformation {
    return this._hisInformationTranslator.toHisInformation(bean);
  }

  // ユーザ TODO：共通化
  protected toUser(bean: IUserBean): User {
    if (!bean) { return null; }

    return new User(
      bean.code,
      bean.name,
      bean.profession ? new Profession(bean.profession, bean.professionName, "") : null,
      bean.kanaName,
      bean.email,
      bean.phone,
    );
  }

  // 診療科 TODO：共通化
  protected toDepartment(bean: IDepartmentBean): Department {
    if (!bean) { return null; }
    return new Department(bean.departmentId, bean.name, bean.shortName, !!bean.isDeleted);
  }

  protected toDepartments(beans: IDepartmentBean[]): Department[] {
    return Enumerable.from(beans)
      .select(bean => this.toDepartment(bean))
      .toArray();
  }

  // プロジェクトチーム
  protected toProjectTeam(bean: IProjectTeamBean): ProjectTeam {
    const translator = new ProjectTeamTranslator();
    return translator.toProjectTeam(bean);
  }

  // フォーマット参照 TODO
  protected toFormatReferences(beans: IFormatInfoBean[]): FormatReference[] {
    if (!beans) { return []; }
    return beans.map(bean => this.toFormatReference(bean));
  }

  // フォーマット参照 TODO
  protected toFormatReference(bean: IFormatInfoBean): FormatReference {
    if (!bean) {
      return null;
    }

    // TODO 暫定変換

    return new FormatReference(
      bean.originalId,
      bean.formatName,      
      new FormatPreviewSource(this._getToken, bean.originalId),
      new FormatThumbnailSource(this._getToken, bean.originalId),
      bean.reportTypeId,
      null,
      null,
      new Company(bean.companyCode, bean.companyName),
      bean.categoryType,
      bean.writeMethodType,
      null,
      null,
      false,
      false,
    );
    // return this._formatTranslator.toFormat(this._getToken, bean); // TODO token
  }

  // 文書参照 TODO:共通化
  protected toDocumentIdentifier(patient: IPatientBean, documentId: number): DocumentIdentifier {
    if (!patient) { return null; }
    if (!documentId) { return null; }

    return new DocumentIdentifier(documentId, patient.patientId);
  }

  protected toDocumentSummary(bean: IDocumentSummaryBean): DocumentSummary {
    if (!bean) { return null; }
    
    return new DocumentSummary(
      bean.documentId,
      bean.patientId,
      bean.edition,
      new DocumentPreviewSource(this._getToken, bean.documentId, bean.edition, bean.patientId),
      new DocumentPrintSource(this._getToken, bean.documentId, bean.edition, bean.patientId),
      bean.isPrintable,
      bean.formatName,
      bean.formatId,
      bean.patientName,
      new Department(
        bean.departmentCode,
        bean.departmentName,
        ""
      ),
      this._dateTranslator.fromDateTimeString(bean.createDateTime),
      bean.createOperatorName,
      bean.createTerminal,
      !!bean.isTemporary,
      bean.note ? bean.note : "",
      bean.readable,
      bean.deletable,
      bean.writable,
      bean.showReadWarning,
      bean.showLoginDialog,
      bean.useAuditLogWrite,
      this._dateTranslator.fromDateTimeString(bean.updateDateTime),
      bean.updateOperatorName,
      this._dateTranslator.fromDateTimeString(bean.contentDateTime),
      bean.reportTypeId,
      bean.reportType,
      bean.published,
      bean.publishedDateTime ? this._dateTranslator.fromDateTimeString(bean.publishedDateTime) : null,
      bean.publishedCount,
      bean.formatOriginalId,
      bean.isDeleted,
      this._dateTranslator.fromDateTimeString(bean.firstDocumentDateTime),
      bean.firstDocumentOperatorName,
      bean.documentType,
      bean.isFixed,
      bean.orderUserCode ? bean.orderUserCode : "",
      bean.orderUserName ? bean.orderUserName : "",
      bean.attendingUserCode ? bean.attendingUserCode : "",
      bean.attendingUserName ? bean.attendingUserName : "",
      bean.defaultCopies,
      bean.companyName,
      bean.isQuotable,
      bean.alternativeFormatOriginalId,
      bean.isUnmanaged,
      bean.isVirtual,
    );
  }

  protected toReferenceFileInformations(beans: IReferenceFileInformationBean[]): ReferenceFileInformation[] {
    return Enumerable.from(beans).select(bean => this.toReferenceFileInformation(bean)).toArray();
  }

  private toReferenceFileInformation(bean: IReferenceFileInformationBean): ReferenceFileInformation {
    if (!bean) { return null; }
    return new ReferenceFileInformation(bean.id, bean.name);
  }

  protected toDocumentSummaryHistory(documentId: number, bean: IDocumentSummaryHistoryBean): DocumentSummaryHistory {
    if (!bean) { return null; }

    return new DocumentSummaryHistory(
      documentId,
      bean.patientId,
      bean.edition,
      new DocumentPreviewSource(this._getToken, documentId, bean.edition, bean.patientId),
      new DocumentPrintSource(this._getToken, documentId, bean.edition, bean.patientId),
      bean.isPrintable,
      bean.archiveId,
      bean.departmentName,
      null,
      bean.createOperatorName,
      bean.createOperatorProfessionName,
      null,
      bean.updateOperatorName,
      bean.published,
      bean.isTemporary,
      false,
      bean.isDeleted,
      bean.isFixed,
      bean.defaultCopies,
      bean.formatName,
      bean.isQuotable,
      bean.alternativeFormatOriginalId,
      bean.isUnmanaged,
      bean.note,
      bean.writable,
      bean.isVirtual,
      bean.orderUserName,
      bean.attendingUserName,
    );
  }

  // HIS項目
  protected toHisInformationItems(beans: IHisInformationItemBean[]): HisInformationItem[] {
    return Enumerable.from(beans).select(bean => this.toHisInformationItem(bean)).toArray();
  }

  private toHisInformationItem(bean: IHisInformationItemBean): HisInformationItem {
    if (!bean) { return null; }
    return new HisInformationItem(bean.keyText, bean.value);
  }

  protected toDateDuration(bean: IDateRangeBean): DateDuration {
    if (!bean) { return null; }

    return new DateDuration(
      this._dateTranslator.fromDateString(bean.from), 
      this._dateTranslator.fromDateString(bean.to)
      );
  }

  toUnmanagedDocumentSummaryInformation(bean: IUnmanagedDocumentSummaryInformationBean): UnmanagedDocumentSummaryInformation {
    if (!bean) { return null; }
    return new UnmanagedDocumentSummaryInformation(
      bean.documentId,
      bean.patientNo,
      bean.formatOriginalId,
      bean.isUnmanaged,
      bean.isQuotable,
      bean.alternativeFormatOriginalId,
    );
  }
}
